/* eslint-disable react/no-array-index-key */
// dependencies
import { arrayMove } from "@dnd-kit/sortable";
import { List, fromJS, isImmutable } from "immutable";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { InputWrapper } from "@performyard/py-components";
// components
import { Avatar } from "@performyard/py-components";

import Checkbox from "../../components/Inputs/Checkbox";
import DatePicker from "../../components/Inputs/DatePicker";
import TextInput from "../../components/Inputs/TextInput";
import Toggle from "../../components/Inputs/ToggleSwitches/ThumbSwitch";
import Config from "./Config";

import { useCompanySettings } from "../../api/hooks";
// hooks
import useEmployeeList from "./hooks/useEmployeeList";

// config
import prependOptions from "../../constants/EmployeePickerPrependReview";
import BeforeUnload from "../BeforeUnload";
import DragAndDrop from "../DragAndDrop";
import DraggableEmployee from "../DragAndDrop/DraggableEmployee";
import EmployeePicker from "../EmployeePicker/compatibility";
import { SingleSelectForm } from "../Inputs/SelectForm";
import SelectInput from "../Inputs/SelectInput";
// styles
import css from "./EasyAddForm.module.scss";

/**
 * Creates a card for a easy add config form.
 * @param {object} props
 */
const EasyAddForm = (props) => {
	const {
		name,
		authors,
		subject,
		signers,
		redacted,
		className = "",
		isEditing = false,
		isDeleted = false,
		lockedFields = List(),
		minimumDueDate = moment(),
		dueDateType,
		authorsDueDate,
		signersDueDate,
		visibleToSubject,
		allowComments,
		invalidMinimumDueDateMessage = null,
		unsaved = false,
		isActive,
		approval = null,
		editableForm = undefined,
		isNewForm,
		onEdit = () => {},
		onDelete = () => {},
		onCancel = () => {},
		onSave = () => {},
	} = props;

	const formIsSelectable = editableForm !== undefined;

	/**
	 * Local states are used to manage the editing state of the form values.
	 */
	// We need a way to store actually saved states
	// because when we save the page will not reload and thus
	// won't be able to update the states properly
	const [savedEasyAddForm, setSavedEasyAddForm] = useState("");
	const [localAuthorValue, setAuthorValue] = useState(authors);
	const [authorValue, setAuthorSelect] = useState("");
	const [subjectType, setSubjectType] = useState(subject);
	const [dateType, setDateType] = useState(dueDateType);
	const [localRedacted, setRedacted] = useState(redacted);
	const [localAuthorsDueDate, setAuthorsDueDate] = useState(authorsDueDate);
	const [localSignersDueDate, setSignersDueDate] = useState(signersDueDate);
	const [localVisibleToSubject, setVisibleToSubject] = useState(visibleToSubject);
	const [localAllowComments, setAllowComments] = useState(allowComments);
	const [localSigners, signerPickerValue, handleSignersChange, removeSigner, setSigners, resetSigners] =
		useEmployeeList(signers);
	const [localIsActive, setIsActive] = useState(isActive);
	const [localApproval, setApproval] = useState(approval);
	// Convert editableForm to Immutable Map if it's a plain object (matching BasicForms/ExternalForms pattern)
	const getImmutableEditableForm = (form) => {
		if (form === null || form === undefined || form === "") {
			return "";
		}
		if (isImmutable(form)) {
			return form;
		}
		if (typeof form === "object" && (form.id || form._id)) {
			// Convert plain object to Immutable Map
			return fromJS({
				id: form.id || form._id,
				name: form.name,
			});
		}
		return "";
	};

	const [localEditableForm, setLocalEditableForm] = useState(getImmutableEditableForm(editableForm));

	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();

	const defaultReviewCycleDueTime = companySettings?.functions?.reviews?.default_review_cycle_due_time ?? 17;

	useEffect(() => {
		let filtered = [];
		if (typeof authors === "string") {
			// an edge case fixed in PY-6095 may have resulted in some of these being saved as strings like "anyEmployee" instead of the expected object format
			filtered = [
				Object.entries(Config.convertAuthorSelect).find(([_key, value]) => value === authors)?.[0] || "",
			];
		} else {
			const authorKeys = authors.keySeq().toArray();
			filtered = authorKeys.filter((key) => authors.get(key));
		}
		// set authorValue for non local easy add configs
		const authorSelectResult = Config.convertAuthorSelect[filtered[0]];
		handleSelectAuthor(authorSelectResult, subjectType);

		// convert approval values
		const approvalValues = Object.values(Config.selectApprovalFormat);
		const approvalKeys = Object.keys(Config.selectApprovalFormat);
		if (localApproval !== null) {
			const index = approvalValues.indexOf(localApproval);
			setApproval(approvalKeys[index]);
		} else if (localApproval === null || !localApproval) {
			setApproval("noApprovalRequired");
		}

		// create a saved cache state so when we save we can just keep updating
		// because the initial data can be saved and changed but will not be
		// loaded in
		setSavedEasyAddForm({
			authorSelectResult,
			savedAuthorValue: localAuthorValue,
			subject: subjectType,
			dueDateType,
			authorsDueDate,
			signersDueDate,
			approval:
				localApproval !== null ? approvalKeys[approvalValues.indexOf(localApproval)] : "noApprovalRequired",
			visibleToSubject,
			allowComments,
			redacted,
			isActive,
			editableForm: getImmutableEditableForm(editableForm),
		});
	}, []);

	// if signers are added, set the default signer due date
	// to be one day after author due date
	useEffect(() => {
		if (localSigners.size === 1 && moment.isMoment(localAuthorsDueDate) && moment.isMoment(localSignersDueDate)) {
			if (localSignersDueDate.isBefore(localAuthorsDueDate)) {
				setSignersDueDate(localAuthorsDueDate.add(1, "days"));
			}
		}
	}, [localSigners.size]);

	// convert date values, toggle between static and relative
	useEffect(() => {
		// Ensure minimumDueDate is not in the past by taking the max of start date and now
		// This ensures consistent calculations when converting between static and relative
		const now = moment();
		const effectiveMinimumDate = moment.max(minimumDueDate, now);

		// If the type is static and there is no due date set, generate a new due date from the minimum date
		if (dateType === "static" && !moment.isMoment(localAuthorsDueDate)) {
			// Clone the effective minimum date and add the configured number of days and set the time to the default review cycle due time
			const staticAuthorDueDate = effectiveMinimumDate
				.clone()
				.add(localAuthorsDueDate, "days")
				.set({ hour: defaultReviewCycleDueTime, minute: 0, second: 0, millisecond: 0 });
			// If setting the time results in a date before the effective minimum date, add one day
			if (staticAuthorDueDate.isBefore(effectiveMinimumDate)) {
				staticAuthorDueDate.add(1, "days");
			}
			// Set the author due date state
			setAuthorsDueDate(staticAuthorDueDate);

			// Clone the author due date and add the configured number of days and set the time to the default review cycle due time
			const staticSignerDueDate = staticAuthorDueDate
				.clone()
				.add(localSignersDueDate, "days")
				.set({ hour: defaultReviewCycleDueTime, minute: 0, second: 0, millisecond: 0 });
			// Set the signer due date state
			setSignersDueDate(staticSignerDueDate);
		}
		// If they type is relative and there is no days to complete set, set the days to complete to the difference in days or 0
		if (dateType === "relative" && moment.isMoment(localAuthorsDueDate)) {
			// Use effectiveMinimumDate for consistency with static conversion
			const relativeAuthorDueDate = localAuthorsDueDate.diff(effectiveMinimumDate, "days");
			setAuthorsDueDate(relativeAuthorDueDate < 0 ? 0 : relativeAuthorDueDate);

			if (moment.isMoment(localSignersDueDate) && moment.isMoment(localAuthorsDueDate)) {
				const relativeSignerDueDate = localSignersDueDate.diff(localAuthorsDueDate, "days");
				setSignersDueDate(relativeSignerDueDate < 0 ? 0 : relativeSignerDueDate);
			}
		}
	}, [dateType, minimumDueDate, defaultReviewCycleDueTime]);

	/**
	 * The author due date must be the same or before a signer due date if
	 * signers exist.
	 * If it isn't, we don't want the user to be able to save the form.
	 */
	let validDueDates = true;

	if (dateType === "static") {
		if (localSigners.size > 0 && moment.isMoment(localAuthorsDueDate) && moment.isMoment(localSignersDueDate)) {
			validDueDates = localAuthorsDueDate.isSameOrBefore(localSignersDueDate);
		}
	}

	/**
	 * the author due date and the signer due date are the same or greater than
	 * the minimum due date.
	 */

	let isAboveLowerDateBoundary = true;

	// handle static due date
	if (minimumDueDate && (moment.isMoment(localAuthorsDueDate) || moment.isMoment(localSignersDueDate))) {
		if (!localAuthorsDueDate || !localAuthorsDueDate.isSameOrAfter(minimumDueDate)) {
			isAboveLowerDateBoundary = false;
		}
		if (!localSigners || (localSigners.size > 0 && !localSignersDueDate.isSameOrAfter(minimumDueDate))) {
			isAboveLowerDateBoundary = false;
		}
	}

	// handle relative due date
	else if (localSignersDueDate < 0 || localAuthorsDueDate < 0) {
		isAboveLowerDateBoundary = false;
	}

	const formSelected = !(formIsSelectable && localEditableForm === "");

	/**
	 * True if we have at least 1 error.
	 */
	const hasError = [validDueDates, isAboveLowerDateBoundary, formSelected].indexOf(false) >= 0;

	const authorToString = (authorsMap) => {
		const jsAuthorMap = isImmutable(authorsMap) ? authorsMap.toJS() : authorsMap;

		return Object.keys(jsAuthorMap).filter((author) => jsAuthorMap[author] === true)[0];
	};

	// check if there are any differences between local and saved values
	const checkFormChange = () => {
		const selectedAuthor = authorToString(authors);
		let selectedLocalAuthor = localAuthorValue;

		if (localAuthorValue && typeof localAuthorValue !== "string") {
			selectedLocalAuthor = authorToString(localAuthorValue);
		}

		const localForm = localEditableForm ? localEditableForm.get("id", null) : null;
		const savedForm = editableForm ? editableForm.get("id", null) : null;

		// convert relative due dates to int if relative date type
		const localAuthorDueDateValue =
			dateType === "relative" ? parseInt(localAuthorsDueDate, 10) : localAuthorsDueDate;

		const localSignerDueDateValue =
			dateType === "relative" ? parseInt(localSignersDueDate, 10) : localSignersDueDate;

		const allFormAttributes = {
			formChanged: localForm !== savedForm,
			subjectTypeChanged: subject !== subjectType,
			authorsChanged: selectedAuthor !== selectedLocalAuthor,
			signersChanged: JSON.stringify(localSigners) !== JSON.stringify(signers),
			redactedChanged: localRedacted !== redacted,
			approvalChanged: approval !== Config.selectApprovalFormat[localApproval],
			authorsDueDateChanged: localAuthorDueDateValue !== authorsDueDate,
			signersDueDateChanged: localSignerDueDateValue !== signersDueDate,
			visibleToSubjectChanged: localVisibleToSubject !== visibleToSubject,
			allowCommentsChanged: localAllowComments !== allowComments,
			isActiveChanged: localIsActive !== isActive,
		};

		return Object.values(allFormAttributes).some((value) => value === true);
	};

	const handleSave = () => {
		if (!validDueDates) {
			return;
		}

		let authorDueDate = localAuthorsDueDate;
		let signerDueDate = localSigners.size > 0 ? localSignersDueDate : localAuthorsDueDate;

		// default dates to 0 if values deleted
		if (dateType === "relative") {
			// convert date to int
			authorDueDate = parseInt(localAuthorsDueDate, 10);
			signerDueDate = parseInt(localSignersDueDate, 10) + parseInt(localAuthorsDueDate, 10);

			if (localAuthorsDueDate === "") {
				authorDueDate = 0;
				setAuthorsDueDate(0);
			}
			if (localSignersDueDate === "") {
				signerDueDate = 0;
				setSignersDueDate(0);
			}
		}

		onSave(
			{
				redact: localRedacted,
				who_can_add_form: localAuthorValue,
				subject_employee_choose_author: subjectType,
				who_signs_off: localSigners.toJS(),
				due_date_types: dateType,
				author_due_date: authorDueDate,
				signer_due_date: signerDueDate,
				visibility: localVisibleToSubject,
				allow_comments: localAllowComments,
				is_active: localIsActive,
				requiresApproval: Config.selectApprovalFormat[localApproval],
				form: formIsSelectable && localEditableForm !== null ? localEditableForm.toJS() : null,
			},
			unsaved
		);

		// Converts the object of authors into just the value
		// We need to save locally on this component the new saved state
		// since this component will not revalidate the backend call for
		// updated easy add data
		const authorKeys = Object.keys(localAuthorValue);
		const filtered = authorKeys.filter((key) => localAuthorValue[key]);

		setSavedEasyAddForm({
			authorSelectResult: Config.convertAuthorSelect[filtered[0]],
			savedAuthorValue: localAuthorValue,
			subject: subjectType,
			dueDateType: dateType,
			authorsDueDate,
			signersDueDate,
			approval: localApproval,
			visibleToSubject: localVisibleToSubject,
			allowComments: localAllowComments,
			redacted: localRedacted,
			isActive: localIsActive,
			editableForm: formIsSelectable ? localEditableForm : null,
		});
	};

	const handleCancel = () => {
		// reset defaults
		// set authorValue for non local easy add configs
		// read off of the updated easy add data states to go back
		setAuthorSelect(savedEasyAddForm.authorSelectResult);
		setSubjectType(savedEasyAddForm.subject);
		setAuthorValue(savedEasyAddForm.savedAuthorValue);
		resetSigners();
		setRedacted(savedEasyAddForm.redacted);
		setDateType(savedEasyAddForm.dueDateType);
		setAuthorsDueDate(savedEasyAddForm.authorsDueDate);
		setSignersDueDate(savedEasyAddForm.signersDueDate);
		setVisibleToSubject(savedEasyAddForm.visibleToSubject);
		setAllowComments(savedEasyAddForm.allowComments);
		setIsActive(savedEasyAddForm.isActive);
		setApproval(savedEasyAddForm.approval);
		setLocalEditableForm(savedEasyAddForm.editableForm);

		// cancel hook
		onCancel();
	};

	/**
	 * Handles form selection from SingleSelectForm component.
	 * Converts from new format {_id, name} to legacy format {id, name}
	 */
	const handleReviewFormChange = (value) => {
		// Convert from new format {_id, name} to legacy immutable format {id, name}
		if (value) {
			const legacyValue = fromJS({
				id: value._id,
				name: value.name,
			});
			setLocalEditableForm(legacyValue);
		} else {
			setLocalEditableForm(value);
		}
	};

	const handleSelectAuthor = (value, subjectType) => {
		setAuthorSelect(value);
		setAuthorValue({
			any_employee: value === "anyEmployee",
			subject: value === "subject",
			manager_or_chain_of_command: value === "managerChainOfCommand",
			admin_or_group_admin: value === "admin",
		});
		// reset subject type to none when author is changed
		// If author is subject, set subject type to default value of "self"
		if (subjectType) {
			setSubjectType(subjectType);
		} else if (value === "subject") {
			setSubjectType("self");
		} else {
			setSubjectType("");
		}
	};

	const handleSelectApproval = (value) => {
		setApproval(value);
	};

	const handleSelectSubjectType = (value) => {
		setSubjectType(value);
	};

	const onDragEndSigners = (event) => {
		const { active, over } = event;

		if (active.id !== over.id) {
			const signers = localSigners.toJS();
			const activeIndex = signers.findIndex((item, ii) => `${item.id}-${ii}` === active.id);
			const overIndex = signers.findIndex((item, ii) => `${item.id}-${ii}` === over.id);
			const newSigners = arrayMove(signers, activeIndex, overIndex);

			// update store
			setSigners(fromJS(newSigners));
		}
	};

	const formatDraggableItems = (employee, index) => {
		return (
			<DraggableEmployee
				key={employee.id}
				employeeId={employee.id}
				employeeName={employee.name}
				onRemove={() => removeSigner(index)}
				data-testid="draggableSignerItem"
				version="old"
			/>
		);
	};

	/**
	 * The redactedness of an a form can only be edited by an admin.
	 * A group admin cannot toggle the redactedness.
	 * For a group admin and a redacted form -
	 *      always show the green checkmark (even if they are editing)
	 * For a group admin and a not redacted form -
	 *      do not show the field on the card at all.
	 */
	let redactedSection = null;

	if (lockedFields.includes("redactedness")) {
		redactedSection = localRedacted ? (
			<div
				data-testid="redactValue"
				className={`${css.attributeValue} ${localRedacted ? css.isGreen : css.isRed}`}
			>
				<FontAwesomeIcon icon={["fas", "check"]} />
			</div>
		) : null;
	} else {
		redactedSection = isEditing ? (
			<div className={css.toggleWrapper}>
				<Toggle onChange={setRedacted} value={localRedacted} width={40} />
			</div>
		) : (
			<div
				data-testid="redactValue"
				className={`${css.attributeValue} ${localRedacted ? css.isGreen : css.isRed}`}
			>
				{localRedacted ? (
					<FontAwesomeIcon icon={["fas", "check"]} />
				) : (
					<FontAwesomeIcon icon={["fas", "times"]} />
				)}
			</div>
		);
	}

	if (isCompanySettingsLoading) {
		return null;
	}

	return (
		<div
			data-testid="easyAddFormCard"
			className={`${css.templateForm} ${isEditing ? css.templateIsEditing : ""} ${className}`}
		>
			<div className={css.content}>
				<div>
					{formIsSelectable && isDeleted && <div className={css.isDeletedLabel}>Form has been deleted</div>}

					{formIsSelectable && (
						<div data-testid="select-review-form" className={css.attributeContainer}>
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Select a form:</div>
							{isEditing ? (
								<InputWrapper id="addForm">
									<SingleSelectForm
										value={
											localEditableForm && typeof localEditableForm.get === "function"
												? {
														_id: localEditableForm.get("id"),
														name: localEditableForm.get("name"),
													}
												: undefined
										}
										onChange={handleReviewFormChange}
										placeholder="Enter form name..."
									/>
								</InputWrapper>
							) : (
								name
							)}
						</div>
					)}

					{!formIsSelectable && (
						<div data-testid="formName" className={css.formNameContainer}>
							<div className={css.formName}>{name}</div>
							{isDeleted ? <div className={css.isDeletedLabel}>Form has been deleted</div> : null}
						</div>
					)}

					<div data-testid="whoCanAddForm" className={css.attributeContainer}>
						<div className={`${css.attributeLabel} ${css.gridCell}`}>Who can add this form:</div>
						{isEditing ? (
							<div>
								{localAuthorValue ? (
									<div>
										<SelectInput
											onChange={(value) => handleSelectAuthor(value)}
											results={Config.selectAuthorOptions}
											value={authorValue}
										/>
									</div>
								) : (
									<div
										data-testid="addAuthorButton"
										className={`${css.addPersonButton} ${css.addAuthor}`}
										onClick={setAuthorValue(" ")}
									>
										Add Author
									</div>
								)}
							</div>
						) : (
							Config.notEditingDisplayLookup[authorValue]
						)}
					</div>

					{authorValue === "subject" ? (
						<div className={css.attributeContainer}>
							<div data-testid="subjectTypeInput" className={`${css.attributeLabel} ${css.gridCell}`}>
								Author Type:
							</div>
							{isEditing ? (
								<div>
									<SelectInput
										onChange={(value) => handleSelectSubjectType(value)}
										results={Config.subjectTypeOptions}
										value={subjectType}
									/>
								</div>
							) : (
								Config.notEditingDisplayLookup[subjectType]
							)}
						</div>
					) : null}

					<div data-testid="signersContainer" className={`${css.attributeContainer} ${css.multipleValues}`}>
						<div className={`${css.attributeLabel} ${css.gridCell} ${isEditing ? css.editView : ""}`}>
							Who signs off on this form:
						</div>
						<div className={`${css.employeesList} ${css.gridCell} ${isEditing ? css.isEditing : ""}`}>
							{isEditing ? (
								<DragAndDrop
									itemIds={localSigners.map((signer, ii) => `${signer.get("id")}-${ii}`).toJS()}
									draggableItems={localSigners.toJS().map((signer, ii) => ({
										...signer,
										drag_id: `${signer.id}-${ii}`,
									}))}
									keyValue="drag_id"
									handleOnDragEnd={onDragEndSigners}
									formatDraggableItems={formatDraggableItems}
								/>
							) : (
								<div>
									{localSigners.size > 0
										? localSigners.map((employee, ii) => (
												<div
													data-testid="signerItem"
													key={`${employee.get("id")}-${ii}`}
													className={css.employeeWrapper}
												>
													<Avatar
														dangerouslySetClassName={`${css.avatar} ${css.gridCell}`}
														id={employee.get("id")}
														size={20}
													/>
													<div
														className={`${css.employeeName} ${css.gridCell} text_truncate`}
													>
														{employee.get("name")}
													</div>
												</div>
											))
										: "No signers have been assigned"}
								</div>
							)}

							<div data-testid="signerPicker" className={css.editPersonContainer}>
								{isEditing && signerPickerValue !== null ? (
									<EmployeePicker
										prependOptions={prependOptions
											.filterNot((item) => ["direct-reports"].includes(item.get("id")))
											.toJS()}
										value={signerPickerValue}
										onChange={handleSignersChange}
									/>
								) : null}

								{isEditing && signerPickerValue === null ? (
									<div
										data-testid="addSignerButton"
										onClick={() => handleSignersChange("")}
										className={`${css.addPersonButton} ${css.addAuthor}`}
									>
										Add Signer
									</div>
								) : null}
							</div>
						</div>
					</div>
					{!lockedFields.includes("dateType") ? (
						<div className={css.attributeContainer}>
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Due Date Type:</div>
							{isEditing ? (
								<div>
									<Checkbox
										name="staticDate"
										onChange={() => setDateType("static")}
										value={dateType === "static"}
									/>
									<label style={{ marginRight: "10px" }}>Static</label>

									<Checkbox
										name="relativeDate"
										onChange={() => setDateType("relative")}
										value={dateType === "relative"}
									/>
									<label>Relative</label>
								</div>
							) : (
								Config.notEditingDisplayLookup[dateType]
							)}
						</div>
					) : null}

					<div className={css.attributeContainer}>
						<div className={`${css.attributeLabel} ${css.gridCell}`}>Authors due date:</div>
						<div className={css.dateContainer}>
							{dateType === "static" ? (
								<div data-testid="authorDueDate">
									{isEditing ? (
										<DatePicker
											dateEngine="moment"
											hasTime={true}
											defaultTime={17}
											onChange={(value) => setAuthorsDueDate(value)}
											value={
												moment.isMoment(localAuthorsDueDate)
													? localAuthorsDueDate
													: minimumDueDate.clone().add(localAuthorsDueDate, "days")
											}
										/>
									) : (
										localAuthorsDueDate.format("MMM D YYYY h:mm a")
									)}
								</div>
							) : null}
							{dateType === "relative" ? (
								<div style={{ display: "flex", alignItems: "center" }}>
									{isEditing ? (
										<div style={{ width: "45px" }}>
											<TextInput
												type="number"
												value={!moment.isMoment(localAuthorsDueDate) ? localAuthorsDueDate : 0}
												onChange={(value) => {
													if (value >= 0) {
														setAuthorsDueDate(value);
													}
												}}
											/>
										</div>
									) : (
										localAuthorsDueDate
									)}
									<div style={{ marginLeft: "5px" }}>{`Day${
										localAuthorsDueDate === 1 ? "" : "s"
									} from date form added`}</div>
								</div>
							) : null}
						</div>
					</div>

					{localSigners.size > 0 ? (
						<div className={css.attributeContainer}>
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Signers due date:</div>
							<div className={css.dateContainer}>
								{dateType === "static" ? (
									<div data-testid="signerDueDate">
										{isEditing ? (
											<DatePicker
												dateEngine="moment"
												hasTime={true}
												defaultTime={17}
												onChange={(value) => setSignersDueDate(value)}
												value={
													moment.isMoment(localSignersDueDate)
														? localSignersDueDate
														: minimumDueDate.clone().add(localSignersDueDate, "days")
												}
											/>
										) : (
											localSignersDueDate.format("MMM D YYYY h:mm a")
										)}
									</div>
								) : null}
								{dateType === "relative" ? (
									<div style={{ display: "flex", alignItems: "center" }}>
										{isEditing ? (
											<div data-testid="signerDueDateInput" style={{ width: "45px" }}>
												<TextInput
													type="number"
													value={
														!moment.isMoment(localSignersDueDate) ? localSignersDueDate : 0
													}
													onChange={(value) => {
														if (value >= 0) {
															setSignersDueDate(value);
														}
													}}
												/>
											</div>
										) : (
											localSignersDueDate
										)}
										<div style={{ marginLeft: "5px" }}>{`Day${
											localSignersDueDate === 1 ? "" : "s"
										} from author due date`}</div>
									</div>
								) : null}
							</div>
						</div>
					) : null}

					<div className={css.attributeContainer}>
						<div data-testid="approvalContainer" className={`${css.attributeLabel} ${css.gridCell}`}>
							Approval:
						</div>
						{isEditing ? (
							<div>
								<SelectInput
									onChange={(value) => handleSelectApproval(value)}
									results={Config.selectApprovalOptions}
									value={localApproval}
								/>
							</div>
						) : (
							Config.notEditingDisplayLookup[localApproval]
						)}
					</div>

					<div data-testid="visibilityContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
						<div className={`${css.attributeLabel} ${css.gridCell}`}>
							Visible to the subject (when all authors/signers are finished):
						</div>
						{isEditing ? (
							<div className={`${css.toggleWrapper} ${css.gridCell}`}>
								<Toggle onChange={setVisibleToSubject} value={localVisibleToSubject} width={40} />
							</div>
						) : (
							<div
								data-testid="visibilityValue"
								className={`${css.attributeValue} ${css.gridCell} ${
									localVisibleToSubject ? css.isGreen : css.isRed
								}`}
							>
								{localVisibleToSubject ? (
									<FontAwesomeIcon icon={["fas", "check"]} />
								) : (
									<FontAwesomeIcon icon={["fas", "times"]} />
								)}
							</div>
						)}
					</div>

					<div data-testid="allowCommentsContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
						<div className={`${css.attributeLabel} ${css.gridCell}`}>Allow 360 request message:</div>
						{isEditing ? (
							<div className={`${css.toggleWrapper} ${css.gridCell}`}>
								<Toggle onChange={setAllowComments} value={localAllowComments} width={40} />
							</div>
						) : (
							<div
								data-testid="allowCommentsValue"
								className={`${css.attributeValue} ${css.gridCell} ${
									localAllowComments ? css.isGreen : css.isRed
								}`}
							>
								{localAllowComments ? (
									<FontAwesomeIcon icon={["fas", "check"]} />
								) : (
									<FontAwesomeIcon icon={["fas", "times"]} />
								)}
							</div>
						)}
					</div>

					{redactedSection ? (
						<div data-testid="redactedContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
							<div className={css.attributeLabel}>Redact authors:</div>
							{redactedSection}
						</div>
					) : null}
					<div data-testid="activeContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
						<div className={`${css.attributeLabel} ${css.gridCell}`}>Active:</div>
						{/*
						   If this form editor is being displayed in a review template, the parent component should
						   set activeFormToggle as a locked field. We only want the ability to toggle when
						   a review cycle is being created.
						*/}
						{isEditing && !lockedFields.contains("activeFormToggle") ? (
							<div className={`${css.toggleWrapper} ${css.gridCell}`}>
								<Toggle onChange={setIsActive} value={localIsActive} width={40} />
							</div>
						) : (
							<div
								data-testid="activeValue"
								className={`${css.attributeValue} ${css.gridCell} ${
									localIsActive ? css.isGreen : css.isRed
								}`}
							>
								{localIsActive ? (
									<FontAwesomeIcon icon={["fas", "check"]} />
								) : (
									<FontAwesomeIcon icon={["fas", "times"]} />
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			<div data-testid="cardActions" className={css.cardActions}>
				{isEditing ? (
					<div>
						<div
							data-testid="saveButton"
							className={`${css.action} ${css.blue} ${hasError ? css.isDisabled : ""}`}
							onClick={hasError ? () => {} : handleSave}
						>
							{isNewForm ? "Add" : "Update"}
						</div>
						<div data-testid="cancelButton" className={`${css.action} ${css.white}`} onClick={handleCancel}>
							Cancel
						</div>
					</div>
				) : (
					<div>
						<div data-testid="editButton" className={`${css.action} ${css.blue}`} onClick={props.onEdit}>
							Edit
						</div>
						<div data-testid="deleteButton" className={`${css.action} ${css.red}`} onClick={props.onDelete}>
							Delete
						</div>
					</div>
				)}

				<div className={css.cardErrors}>
					{validDueDates ? null : (
						<div className={css.cardError}>
							The author due date must be the same or come before the signer due date.
						</div>
					)}
					{isAboveLowerDateBoundary ? null : (
						<div data-testid="invalidMinimumDueDateMessage" className={css.cardError}>
							{invalidMinimumDueDateMessage}
						</div>
					)}
					{formSelected ? null : <div className={css.cardError}>A form must be selected.</div>}
				</div>
			</div>
			<BeforeUnload checkForUnsavedData={checkFormChange} />
		</div>
	);
};

EasyAddForm.propTypes = {
	/** Handle clicking the edit action */
	onEdit: PropTypes.func,
	/** Handle clicking the delete action */
	onDelete: PropTypes.func,
	/** Handle clicking the cancel action */
	onCancel: PropTypes.func,
	/** Handle clicking the save action */
	onSave: PropTypes.func,
	/** Will put the card in edit mode if true */
	isEditing: PropTypes.bool,
	/** Will put a 'form has been deleted' badge on the card */
	isDeleted: PropTypes.bool,
	/** Additional class */
	className: PropTypes.string,
	/** Will display a green check or red 'x' */
	visibleToSubject: PropTypes.bool.isRequired,
	/** Will display a green check or red 'x' */
	allowComments: PropTypes.bool.isRequired,
	/** Will display a green check or red 'x' */
	redacted: PropTypes.bool.isRequired,
	/** Name of the form */
	name: PropTypes.string.isRequired,
	/** Type of due date: static or relative */
	dueDateType: PropTypes.string.isRequired,
	/** days to complete the form for authors */
	authorsDueDate: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
	/** days to complete the form for signers */
	signersDueDate: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
	/** An object with author types as keys and boolean as value */
	authors: PropTypes.object.isRequired,
	/** If author is subject, subject can either be 'self' or 'any employee'  */
	subject: PropTypes.string.isRequired,
	/** An array of signer objects with an 'id' and 'name' */
	signers: PropTypes.instanceOf(List).isRequired,
	/** A map of field names and true or false.
	 * a value of true means that the field cannot
	 * be edited by the current user. */
	lockedFields: PropTypes.instanceOf(List),
	/** the minimum due date allowed */
	minimumDueDate: PropTypes.instanceOf(moment),
	/** the message to be displayed if the min due date has been broken */
	invalidMinimumDueDateMessage: PropTypes.string,
	/** If easy add form is new, it will have unsaved=true, */
	unsaved: PropTypes.bool,
	/** Active state of easy add form */
	isActive: PropTypes.bool.isRequired,
	/** requires approval to add form */
	approval: PropTypes.string,
	/** the selected form for the dropdown. If this value is `undefined`, then
	 * the dropdown is not displayed. This can also be null if nothing is
	 * selected. */
	editableForm: PropTypes.exact({
		_id: PropTypes.string,
		name: PropTypes.string,
	}),
	isNewForm: PropTypes.bool.isRequired,
};

export default EasyAddForm;
